import type { HttpClient } from '@wix/cli-http-client';
import pRetry from 'p-retry';
import type { Options } from 'p-retry';
import pWaitFor from 'p-wait-for';
import {
  createHttpClient,
  isHttpError,
  isHttpApplicationError,
} from '@wix/cli-http-client';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { getTestOverrides } from '@wix/cli-test-overrides';
import {
  AppType,
  RcLabel,
  DeploymentOperation,
} from '@wix/ambassador-velo-apps-v1-app/types';
import { createApp, deployApp } from '@wix/ambassador-velo-apps-v1-app/http';
import {
  listFiles,
  updateFiles,
} from '@wix/ambassador-velo-ide-v1-gridapp-filestructure/http';
import { GridAppLayout } from '@wix/ambassador-velo-ide-v1-gridapp-filestructure/types';
import {
  resolveNpmDependencies,
  getResolveNpmDependenciesResult,
} from '@wix/ambassador-velo-npm-v1-npm-package-info/http';
import type { InitGitHubRepositoryRequest } from '@wix/ambassador-velo-github-v1-github-onboarding-status/types';
import {
  Label,
  OnboardingState,
} from '@wix/ambassador-velo-github-v1-github-onboarding-status/types';
import {
  createAppSchema,
  listFilesSchema,
  deployAppSchema,
  getWixLockFileSchema,
  resolveNpmDependenciesSchema,
  getResolveNpmDependenciesResultSchema,
  gitHubOnboardingStatusResponseSchema,
} from './schemas.js';

import type {
  AppContent,
  DeploymentSourceData,
  UpdateFilesContent,
  NpmPackage,
} from './types.js';

export interface GitHubRepoInfo {
  status: OnboardingState;
  repoName: string;
  cloneUrl: string;
  htmlUrl: string;
}

const { minRetryTimeout, pollInterval, pollTimeout } = getTestOverrides();

export class VeloClient {
  private readonly retryOptions: Options;

  constructor(private readonly httpClient: HttpClient) {
    this.retryOptions = {
      retries: 3,
      minTimeout: minRetryTimeout,
      maxTimeout: 3 * 1000,
    };
  }

  createMutableApp = async (content: AppContent) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            createApp({
              app: { mutable: true, appType: AppType.VELO_ISOLATED },
              content,
            })
          ),
        this.retryOptions
      );

      return createAppSchema.parse(data).app;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToCreateVeloApp(),
        cause: e,
      });
    }
  };

  listAppFiles = async (appId: string) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            listFiles({
              gridAppId: appId,
              layout: GridAppLayout.GITHUB,
              hideDeletedPages: true,
              includeNewPages: false,
            })
          ),
        this.retryOptions
      );
      return listFilesSchema.parse(data);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetFilesFromVelo(),
        cause: e,
      });
    }
  };

  updateAppFiles = async (appId: string, content: UpdateFilesContent) => {
    try {
      await pRetry(
        () =>
          this.httpClient.request(
            updateFiles({
              ...content,
              gridAppId: appId,
            })
          ),
        this.retryOptions
      );
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToUpdateVeloAppFiles(),
        cause: e,
      });
    }
  };

  deploySitePreview = async (deploymentSourceData: DeploymentSourceData) => {
    try {
      const { data } = await this.httpClient.request(
        deployApp({
          ...deploymentSourceData,
          deploymentOperation: DeploymentOperation.RC,
          appType: AppType.VELO,
          rcDeploymentParams: {
            rcLabel: RcLabel.WIX_CLI,
          },
        })
      );

      return deployAppSchema.parse(data).deploymentInfo;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToDeploySitePreview(),
        cause: e,
      });
    }
  };

  generateWixLockFile = async (deps: NpmPackage[]) => {
    const jobId = await this.resolveNpmDependencies(deps);
    const wixLockFileUrl = await this.getResolveNpmDependenciesResult(jobId);

    return this.getWixLockFile(wixLockFileUrl);
  };

  private resolveNpmDependencies = async (npmPackageInfos: NpmPackage[]) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(resolveNpmDependencies({ npmPackageInfos })),
        this.retryOptions
      );

      const { jobId } = resolveNpmDependenciesSchema.parse(data);

      return jobId;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToResolveNpmDependencies(),
        cause: e,
      });
    }
  };

  private getResolveNpmDependenciesResult = async (jobId: string) => {
    try {
      return await pWaitFor(
        async () => {
          try {
            const { data } = await pRetry(
              () =>
                this.httpClient.request(
                  getResolveNpmDependenciesResult({ jobId })
                ),
              this.retryOptions
            );

            const { result } =
              getResolveNpmDependenciesResultSchema.parse(data);

            return pWaitFor.resolveWith(result.npmDependenciesLockFile);
          } catch (e) {
            // Continue polling
            if (isHttpError(e) && e.response?.status === 428) {
              return false;
            }
            // Unexpected error - stop polling
            throw e;
          }
        },
        {
          // Poll each 2 seconds
          interval: pollInterval ?? 2 * 1000,
          // Fail if 5 minutes passed
          timeout: pollTimeout ?? 5 * 60 * 1000,
        }
      );
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetResolveNpmDependenciesResult(),
        cause: e,
        info: { jobId },
      });
    }
  };

  private getWixLockFile = async (url: string) => {
    try {
      const standaloneHttpClient = createHttpClient({ type: 'standalone' });

      const { data } = await pRetry(
        () => standaloneHttpClient.get(url),
        this.retryOptions
      );

      return getWixLockFileSchema.parse(data);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToFetchWixLockFile(),
        cause: e,
      });
    }
  };

  getGitHubOnboardingStatus = async (
    projectId: string
  ): Promise<GitHubRepoInfo | null> => {
    try {
      const { data } = await this.httpClient
        .request({
          method: 'GET',
          url: `https://manage.wix.com/_api/wix-code-github-onboarding/v1/onboarding-status?projectId=${projectId}`,
        })
        .catch((error: unknown) => {
          if (isHttpError(error) && isHttpApplicationError(error)) {
            return { data: null };
          }
          throw error;
        });

      if (!data) {
        return null;
      }

      const parsed = gitHubOnboardingStatusResponseSchema.parse(data);
      const { gitHubOnboardingStatus } = parsed;

      if (
        gitHubOnboardingStatus.onboardingState ===
        OnboardingState.GITHUB_REPOSITORY_CREATED
      ) {
        const { repository } = gitHubOnboardingStatus.gitHubRepositoryCreated;

        return {
          status: OnboardingState.GITHUB_REPOSITORY_CREATED,
          repoName: repository.repositoryName,
          cloneUrl: repository.cloneUrl,
          htmlUrl: repository.htmlUrl,
        };
      }

      if (
        gitHubOnboardingStatus.onboardingState ===
        OnboardingState.GITHUB_ENABLED
      ) {
        const { repository } = gitHubOnboardingStatus.gitHubEnabled;

        return {
          status: OnboardingState.GITHUB_ENABLED,
          repoName: repository.repositoryName,
          cloneUrl: repository.cloneUrl,
          htmlUrl: repository.htmlUrl,
        };
      }

      return null;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetGitHubOnboardingStatus({ projectId }),
        cause: e,
      });
    }
  };

  initGitHubRepository = async (projectId: string) => {
    try {
      await this.httpClient.request<InitGitHubRepositoryRequest>({
        method: 'POST',
        url: `https://manage.wix.com/_api/wix-code-github-onboarding/v1/github-repositories/init-velo`,
        data: {
          label: Label.WIX_VIBE,
          projectId,
        },
      });
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToInitGitHubRepository({ projectId }),
        cause: e,
      });
    }
  };
}
